import type { IconProps } from '@phosphor-icons/react'
import type {
  ComponentPropsWithRef,
  ComponentType,
  ElementType,
  PropsWithChildren,
  ReactNode,
} from 'react'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'

import type { DistributiveOmit } from '../../polymorphic-props'

type Props = VariantProps<typeof variants> & {
  Icon?: ComponentType<IconProps>
  children: ReactNode
  className?: string
  dataTestId?: string
}

type FlagAsProps<TAs extends ElementType> = {
  as?: TAs
} & DistributiveOmit<ComponentPropsWithRef<ElementType extends TAs ? 'div' : TAs>, 'as'>

const variants = tv({
  base: 'font-base-copy bg-sand flex h-min w-fit items-center gap-2 whitespace-nowrap rounded-sm px-2 py-1',
  variants: {
    variant: {
      info: 'bg-lightblue',
      success: 'bg-success-bg text-primary',
      warning: 'bg-warning-bg text-warning-text',
      error: 'bg-error-bg text-error',
      outline: 'bg-white text-primary border border-primary-green/15',
      interactive:
        'bg-white text-primary border border-primary-green/15 hover:layer-bg-hover-light',
    },
    disabled: {
      true: 'pointer-events-none opacity-30',
    },
  },
})

export function Flag<TAs extends ElementType>(props: PropsWithChildren<Props> & FlagAsProps<TAs>) {
  const {
    ref,
    Icon,
    children,
    className,
    variant,
    disabled,
    dataTestId,
    as: Root = 'div',
    ...rest
  } = props
  const classes = variants({ variant, disabled, className })

  return (
    <Root className={classes} data-testid={dataTestId} ref={ref} {...rest}>
      {Icon && <Icon className="size-4" />}
      {children}
    </Root>
  )
}
